import axios from "axios"
import {LOGGING_URL} from "../globals"
import {find, forEach} from "lodash/collection"
import {unset} from "lodash/object"
import jwtDecode from "jwt-decode"
import {cloneDeep} from "lodash/lang"
import projectErrors from "../api/errors/project"
import notify, {NotificationOptions} from "./notify"

const NM_SERVICE = "HorusPlatformFrontend"

const LOG_LEVEL = {
  CRITICAL: "CRITICAL",
  ERROR: "ERROR",
  WARNING: "WARNING",
  INFO: "INFO",
  DEBUG: "DEBUG",
  NOTSET: "NOTSET",
}

const unsetKeys = [
  "config.headers.Authorization",
  "config.data.card_data",
  "config.data.username",
  "config.data.password",
]

const URL = {
  LOGS: "/logs/",
}

const notifyErrorMap = [
  ...projectErrors
]

const axiosInstance = axios.create({
  baseURL: LOGGING_URL,
})
axiosInstance.interceptors.response.use(null, handleError)

const handleNotifyError = (error) => {
  const key = error.response.data.error
  const normalizedMessage = find(notifyErrorMap, ["key", key])

  if (!normalizedMessage) return

  if (normalizedMessage.notify)
    notify(new NotificationOptions({
      message: normalizedMessage.message,
      type: normalizedMessage.type
    }))
}

const handleLogMessage = (logError, logLevel) => {
  const jsonData = cloneDeep(logError["error"].toJSON())
  if (jsonData.hasOwnProperty("config"))
    if (jsonData["config"].hasOwnProperty("headers"))
      if (jsonData["config"]["headers"].hasOwnProperty("Authorization")) {
        const decodedJwt = jwtDecode(jsonData["config"]["headers"]["Authorization"])
        jsonData["cd_user"] = decodedJwt["sub"]
      }

  forEach(unsetKeys, key => unset(jsonData, key))

  const logData = {
    nm_service: NM_SERVICE,
    nm_level: logLevel,
    nm_normalized: logError.config.url,
    str_description: logError.message,
    json_data: jsonData,
  }
  logMessage(logData)
}

export const logMessage = (logData = {}) => {
  return axiosInstance.post(
    URL.LOGS,
    logData,
  )
}

export class BaseError extends Error {
  constructor(message, errorObject) {
    super(message)

    this.error = errorObject
    this.name = "BaseError"
  }
}

export class NetworkError extends BaseError {
  constructor(message, errorObject) {
    super(message, errorObject)

    this.name = "NetworkError"
  }
}

export class ResponseError extends BaseError {
  constructor(message, statusCode, responseData, responseHeaders, errorObject) {
    super(message, errorObject)

    this.name = "ResponseError"
    this.statusCode = statusCode
    this.responseData = responseData
    this.responseHeaders = responseHeaders
  }
}

export function handleError(error) {
  let logError = null

  let logLevel = LOG_LEVEL.NOTSET
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    let message = error.response.data.message
    if (!message) message = "Non 2xx response from server"

    logError = new ResponseError(
      message,
      error.response.status,
      error.response.data,
      error.response.headers,
      error,
    )

    switch (error.response.status) {
      case 400:
        logLevel = LOG_LEVEL.WARNING
        break
      case 500:
        logLevel = LOG_LEVEL.ERROR
        break
      default:
        return
    }

    handleNotifyError(error)
  } else if (error.request) {
    return Promise.reject(NetworkError("Network error when trying to do the request", error))
  } else {
    logError = new BaseError(`Something went wrong setting up axios request: ${error.message}`, error)
  }

  handleLogMessage(logError, logLevel)
  return Promise.reject(error)
}

export function normalizeError(error) {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    return new ResponseError(
      "Non 2xx response from server",
      error.response.status,
      error.response.data,
      error.response.headers,
      error,
    )
  } else if (error.request) {

    return new NetworkError("Network error when trying to do the request", error)

  }

  return new BaseError(
    `Something went wrong setting up axios request: ${error.message}`, error,
  )
}
